import * as React from "react"
import { Link, useNavigate } from "react-router-dom"
import KravMagaSantos from "../images/KravMagaSantos.svg"
import AuthShell from "../components/AuthShell"
import { getGraduacoes, registerStudent } from "../auth/api"
import { useAuth } from "../auth/AuthContext"

const inputClass =
  "w-full border-2 border-gray-200 rounded-xl text-center p-2 mt-1 bg-transparent placeholder:text-gray-400"

export default function StudentRegisterPage() {
  const navigate = useNavigate()
  const { completeAuth } = useAuth()

  const [graduacoes, setGraduacoes] = React.useState(["fase inicial"])
  const [form, setForm] = React.useState({
    nomeCompleto: "",
    cpf: "",
    dataNascimento: "",
    celular: "",
    graduacao: "fase inicial",
    ultimoExameEm: "",
    email: "",
    password: "",
    confirmPassword: "",
  })
  const [error, setError] = React.useState("")
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    getGraduacoes()
      .then((list) => {
        if (list?.length) {
          setGraduacoes(list)
        }
      })
      .catch(() => {
        // mantém lista padrão
      })
  }, [])

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError("")

    if (form.password !== form.confirmPassword) {
      setError("As senhas não coincidem.")
      return
    }

    if (form.password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.")
      return
    }

    try {
      setLoading(true)
      const data = await registerStudent({
        nomeCompleto: form.nomeCompleto.trim(),
        cpf: form.cpf.replace(/\D/g, ""),
        dataNascimento: form.dataNascimento,
        celular: form.celular.replace(/\D/g, ""),
        graduacao: form.graduacao,
        ultimoExameEm: form.ultimoExameEm || null,
        email: form.email.trim(),
        password: form.password,
      })
      completeAuth(data)
      navigate("/", { replace: true })
    } catch (err) {
      setError(err?.message || "Não foi possível concluir o cadastro.")
    } finally {
      setLoading(false)
    }
  }

  const formatarCPF = (valor) => {
    const numeros = valor.replace(/\D/g, "")
    if (numeros.length <= 3) return numeros
    if (numeros.length <= 6) return `${numeros.slice(0, 3)}.${numeros.slice(3)}`
    if (numeros.length <= 9)
      return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6)}`
    return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6, 9)}-${numeros.slice(9, 11)}`
  }

  return (
    <AuthShell showRegisterLink={false}>
      <form onSubmit={handleSubmit} className="w-full max-w-lg px-4 h-dvh">
        <h1 className="text-4xl text-center font-oswald font-semibold text-gray-50 lg:text-cor-base mb-4 drop-shadow-lg">
          Cadastro de aluno
        </h1>

        <div className="bg-slate-50 rounded-3xl py-6 px-6 lg:px-8 lg:bg-[#abb2b9] drop-shadow-xl">
          <div className="flex justify-center mb-4">
            <div className="justify-center h-32 w-32 mb-6 object-contain">
              <img
                src={KravMagaSantos}
                alt="Krav Maga Logo"
                className="object-contain drop-shadow-lg"
              />
            </div>
          </div>

          <p className="text-cor-base text-lg mb-4 drop-shadow-lg">
            Preencha seus dados. Este formulário cadastra apenas alunos.
          </p>

          <div className="grid gap-3 text-cor-base">
            <Field label="Nome completo">
              <input
                className={inputClass}
                value={form.nomeCompleto}
                onChange={(e) => updateField("nomeCompleto", e.target.value)}
                required
              />
            </Field>

            <Field label="CPF">
              <input
                className={inputClass}
                value={formatarCPF(form.cpf)}
                onChange={(e) => {
                  const apenasNumeros = e.target.value.replace(/\D/g, "")
                  if (apenasNumeros.length <= 11) {
                    updateField("cpf", apenasNumeros)
                  }
                }}
                placeholder="Somente números"
                inputMode="numeric"
                maxLength={14}
                required
              />
            </Field>

            <Field label="Data de nascimento">
              <input
                type="date"
                className={inputClass}
                value={form.dataNascimento}
                onChange={(e) => updateField("dataNascimento", e.target.value)}
                required
              />
            </Field>

            <Field label="Celular">
              <input
                className={inputClass}
                value={form.celular}
                onChange={(e) => {
                  const apenasNumeros = e.target.value.replace(/\D/g, "")
                  if (apenasNumeros.length <= 11) {
                    updateField("celular", apenasNumeros)
                  }
                }}
                placeholder="DDD + número"
                inputMode="tel"
                maxLength={11}
                required
              />
            </Field>

            <Field label="Graduação">
              <select
                className={inputClass}
                value={form.graduacao}
                onChange={(e) => updateField("graduacao", e.target.value)}
                required>
                {graduacoes.map((g) => (
                  <option key={g} value={g}>
                    {g.charAt(0).toUpperCase() + g.slice(1)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Data do último exame (opcional)">
              <input
                type="date"
                className={inputClass}
                value={form.ultimoExameEm}
                onChange={(e) => updateField("ultimoExameEm", e.target.value)}
              />
              <p className="text-sm mt-1 opacity-80">
                Deixe em branco se nunca fez exame.
              </p>
            </Field>

            <Field label="E-mail">
              <input
                type="email"
                className={inputClass}
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                required
              />
            </Field>

            <Field label="Senha">
              <input
                type="password"
                className={inputClass}
                value={form.password}
                onChange={(e) => updateField("password", e.target.value)}
                required
              />
            </Field>

            <Field label="Confirmar senha">
              <input
                type="password"
                className={inputClass}
                value={form.confirmPassword}
                onChange={(e) => updateField("confirmPassword", e.target.value)}
                required
              />
            </Field>
          </div>

          {error ? (
            <p className="text-red-700 text-center mt-4 font-semibold">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full py-3 text-xl bg-cor-base text-white rounded-xl hover:scale-[1.01] transition-all disabled:opacity-60">
            {loading ? "Cadastrando..." : "Cadastrar"}
          </button>

          <p className="text-center mt-4 text-cor-base lg:hidden">
            <Link to="/login" className="font-semibold hover:underline">
              Já tenho conta — voltar ao login
            </Link>
          </p>
        </div>
      </form>
    </AuthShell>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-base font-medium">{label}</label>
      {children}
    </div>
  )
}
